
import { Sounds } from '../../../@resources/sounds';
import { Arr } from '../../../CodesForFoundation';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { inCommands } from '../../../concepts/commands/CommandRegistry';
import { ObjectManager } from '../../../concepts/objects';
import { Colors } from '../../../ui';
import { ProfileModel } from '../../ProfileModel';
import { resolveSubject, resolveSubjects } from '../../canvas/commands/CommandUtil';
import { clearSelection } from '../../canvas/commands/SelectionCommands';
import { Physical } from '../../canvas/entities/Physical';
import { Physicals } from '../../canvas/entities/Physicals';
import { SpatialIndex } from '../../canvas/utilities/SpatialIndex';
import { FlexSorter } from '../autoLayout/FlexLayout';
import { Graphic } from '../graphics/Graphic';
import { Sticky } from '../paper/Sticky';
import { Token } from '../tokens/Token';
import { Voter } from './Voter';
import { VoterController } from './VoterController';
import { ItemPrototype, VoterItem } from './VoterItem';
import { VoterVote } from './VoterVote';

const voterItemSpecFrom = (elmt: Physical) => {
    const sticky = elmt.as(Sticky);
    if (!!sticky?.content?.trim()) return { text: sticky.content.trim(), color: sticky.background || sticky.autoColor };
    const token = elmt.as(Token);
    if (!!token?.value?.trim()) return { text: token.value.trim(), color: token.background };
    const graphic = elmt.as(Graphic);
    if (!!graphic?.url) return { text: `(${graphic.url})`, color: Colors.text };
    return null;
};

export const createVoter = defineCommand(
    'createVoter',
    'Create: Voter...',
    (cc) => {
        Physicals.createAtCameraPosition(cc, Voter, x => {
            x.width = 140;
            x.height = 140;
            x.sound = Arr.random([
                Sounds.Buzzer1,
                Sounds.Buzzer2,
                Sounds.Buzzer3,
                Sounds.Buzzer4,
                Sounds.Buzzer5,
            ]);
        });
    },
);

export const voterLoad = defineCommand(
    'voterLoad',
    'Voter: Load...',
    (cc, [targetId, ...items]: [string, ...(string | ItemPrototype)[]]) => {

        const objects = cc.resolve(ObjectManager);
        const voter = resolveSubject(cc, targetId)?.as(Voter);
        if (!voter) return;

        objects.update(u => {

            // Empty
            for (const ch of voter.children) {
                u.delete(ch.removeSelf());
            }

            // Load
            for (const tm of items) {
                const item = u.create(VoterItem);
                if (typeof tm === 'string') {
                    item.text = tm;
                }
                else {
                    item.text = tm.text;
                    item.color = tm.color;
                }
                voter.add(item);
            }

            // Reset if not voting
            voter.state = 'voting';

        });
    }
);

export const voterReset = defineCommand(
    'voterReset',
    'Voter: Reset...',
    (cc, [targetId]: string[]) => {

        const objects = cc.resolve(ObjectManager);
        const voter = resolveSubject(cc, targetId)?.as(Voter);
        if (!voter) return;
        if (!voter.votes.length) return;

        objects.update(u => {
            for (const vv of Array.from(voter.votes)) {
                u.delete(vv.removeSelf());
            }
            voter.state = 'voting';
        });
    }
);

export const voterVote = defineCommand(
    'voterVote',
    'Voter: Vote',
    (cc, [targetId, idOrIndex]: [string, string | number]) => {

        const me = cc.resolve(ProfileModel);
        const objects = cc.resolve(ObjectManager);
        const voter = resolveSubject(cc, targetId)?.as(Voter);
        if (!voter) return;

        const item = !isNaN(idOrIndex as any)
            ? voter.items[idOrIndex]
            : voter.items.find(x => x.id === idOrIndex);
        if (!item) return;

        objects.update(u => {

            cc.dispatch(voterUnvote, [targetId]);

            // Add vote
            const vote = u.create(VoterVote);
            vote.itemId = item.id;
            vote.userId = me.id;
            vote.created = new Date();
            voter.add(vote);

        });
    }
);

export const voterUnvote = defineCommand(
    'voterUnvote',
    'Voter: Unvote',
    (cc, [targetId]: [string]) => {

        const me = cc.resolve(ProfileModel);
        const objects = cc.resolve(ObjectManager);
        const voter = resolveSubject(cc, targetId)?.as(Voter);
        if (!voter) return;

        objects.update(u => {
            // Remove any votes we've made
            for (const vv of voter.votes) {
                if (vv.userId === me.id) {
                    u.delete(vv.removeSelf());
                }
            }
        });
    }
);

export const voterInsert = defineCommand(
    'voterInsert',
    'Voter: Insert',
    (cc, [targetId, index, elmtIds]: [string, number, string | string[]]) => {

        const spatial = cc.call(SpatialIndex);
        const objects = cc.call(ObjectManager);

        const voter = objects.getMaybe(targetId, Voter);
        const elmts = FlexSorter.sort('row', resolveSubjects(cc, elmtIds));
        if (!voter || !elmts.length) return;

        const specs = elmts.map(x => voterItemSpecFrom(x)).filter(x => !!x);
        cc.dispatch(clearSelection);

        objects.update(u => {

            const before = voter.items[index];

            // Convert elmts to items
            for (const tm of specs) {
                const item = u.create(VoterItem);
                item.text = tm.text;
                item.color = tm.color;
                voter.addBefore(before, item);
            }

            // Remove elmts
            for (const el of elmts) {
                u.delete(el.removeSelf());
                // TODO: Need to immediately invalidate until we can improve how spatial indexing is updated
                spatial.invalidate(el.id);
            }

        });
    }
);

export const voterResetOrReveal = defineCommand(
    'voterResetOrReveal',
    'Voter: Reset or Reveal',
    (cc, [targetId]: [string]) => {

        const objects = cc.resolve(ObjectManager);
        const voter = objects.getMaybe(targetId, Voter);
        const ctrl = cc.resolve(VoterController);

        if (!voter) return;

        objects.update(u => {
            const { votes } = voter;
            if (voter.state === 'voting') {
                if (votes.length === 0) return;
                voter.state = 'results';

                ctrl.displayResults(voter);
            }
            else {
                for (const v of votes) {
                    u.delete(v.removeSelf());
                }
                voter.state = 'voting';
            }
        });
    }
);

export const inVoterCommands = () => inCommands(
    createVoter,
    voterLoad,
    voterInsert,
    voterReset,
    voterVote,
    voterUnvote,
);

